import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import api from "../../services/api";
import { colors, DECISIONS } from "./constants";
import MakeupScheduleFields from "./Makeupschedulefields";
import AdjustmentFields from "./Adjustmentfields";

// NOTE: makeup_date / service_slot_id are sent in the payload below in
// anticipation of the backend's makeup-insertion API, but they are NOT
// part of the CarryForwardDecision schema as last confirmed:
//   { status, adjustment_month, adjustment_year, remarks }
// Until that schema is updated, these two fields will likely be ignored
// or rejected server-side. Remove/update once the real contract lands.
export default function CarryForwardDecisionDialog({
  open,
  session,
  branchId,
  onClose,
  onSuccess,
  onError,
}) {
  const [decision, setDecision] = useState("");
  const [makeupDate, setMakeupDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [adjustmentMonth, setAdjustmentMonth] = useState("");
  const [adjustmentYear, setAdjustmentYear] = useState(new Date().getFullYear());
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resetFields = () => {
    setDecision("");
    setMakeupDate("");
    setSelectedSlot(null);
    setAdjustmentMonth("");
    setAdjustmentYear(new Date().getFullYear());
    setRemarks("");
  };

  const handleClose = () => {
    if (submitting) return;
    resetFields();
    onClose();
  };

  const isValid = useMemo(() => {
    if (!decision) return false;
    if (decision === "MAKEUP") return Boolean(makeupDate && selectedSlot);
    if (decision === "ADJUSTMENT") return Boolean(adjustmentMonth && adjustmentYear);
    return true;
  }, [decision, makeupDate, selectedSlot, adjustmentMonth, adjustmentYear]);

  const submitDecision = async () => {
    if (!session || !isValid) return;
    setSubmitting(true);
    try {
      const payload = {
        status: decision,
        remarks: remarks || null,
        adjustment_month: decision === "ADJUSTMENT" ? Number(adjustmentMonth) : null,
        adjustment_year: decision === "ADJUSTMENT" ? Number(adjustmentYear) : null,
        // See NOTE at top of file — pending backend schema support.
        makeup_date: decision === "MAKEUP" ? makeupDate : null,
        service_slot_id: decision === "MAKEUP" ? Number(selectedSlot) : null,
      };

      const response = await api.post(
        `/app/patient/carry-forward/${session.id}/decision`,
        payload,
      );

      onSuccess?.(session.id, response?.data, decision);
      resetFields();
    } catch (error) {
      console.error("Error saving carry-forward decision:", error);
      onError?.(error?.response?.data?.detail || "Unable to save decision.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: colors.text }}>
          Carry-Forward Decision
        </Typography>
        {session && (
          <Typography sx={{ fontSize: 12, color: colors.secondary, mt: 0.5 }}>
            {session.patient_name} \u00b7 {session.service_name}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: colors.secondary,
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          Select Decision
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
          {DECISIONS.map((item) => {
            const selected = decision === item.value;
            return (
              <Button
                key={item.value}
                fullWidth
                variant={selected ? "contained" : "outlined"}
                onClick={() => setDecision(item.value)}
                sx={{
                  height: 42,
                  textTransform: "none",
                  borderRadius: 2,
                  fontSize: 12,
                  fontWeight: 700,
                  color: selected ? "#fff" : colors.text,
                  backgroundColor: selected ? colors.primary : "#fff",
                  borderColor: colors.border,
                  "&:hover": {
                    backgroundColor: selected ? colors.primaryDark : colors.primaryLight,
                  },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Box>

        {decision === "MAKEUP" && (
          <MakeupScheduleFields
            branchId={branchId}
            serviceId={session?.service_id}
            makeupDate={makeupDate}
            selectedSlot={selectedSlot}
            onDateChange={setMakeupDate}
            onSlotChange={setSelectedSlot}
          />
        )}

        {decision === "ADJUSTMENT" && (
          <AdjustmentFields
            month={adjustmentMonth}
            year={adjustmentYear}
            onMonthChange={setAdjustmentMonth}
            onYearChange={setAdjustmentYear}
          />
        )}

        {decision === "REJECTED" && (
          <Box sx={{ p: 1.5, mb: 2.5, borderRadius: 2, backgroundColor: colors.redLight }}>
            <Typography sx={{ fontSize: 12, color: colors.red, fontWeight: 600 }}>
              This session will be rejected and will not be carried forward.
            </Typography>
          </Box>
        )}

        {decision && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add remarks..."
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isValid || submitting}
          onClick={submitDecision}
          startIcon={submitting ? <CircularProgress size={15} color="inherit" /> : null}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            backgroundColor: colors.primary,
            "&:hover": { backgroundColor: colors.primaryDark },
          }}
        >
          {submitting
            ? "Saving..."
            : decision === "MAKEUP"
            ? "Schedule Makeup"
            : decision === "ADJUSTMENT"
            ? "Save Adjustment"
            : "Reject Session"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
